import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Plus, ChevronDown, ChevronUp, Lock } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useState } from "react";
import { ScrollingCarousel } from '@/components/ui/scrolling-carousel';
import { cn } from '@/lib/utils';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { logger } from "@/lib/logger";

export const THEMES = [
  "friendship",
  "love",
  "betrayal",
  "redemption",
  "growth",
  "power",
  "justice",
  "family",
  "courageBravery",
  "magicMythology",
  "survival",
  "warConflict",
  "freedomRebellion",
  "violenceRevenge",
  "passionEroticism",
] as const;

interface ThemeSelectionProps {
  selectedThemes: string[];
  onThemeChange: (theme: string[]) => void;
  customThemes: string[];
  onCustomThemeChange: (themes: string[]) => void;
  disabledThemes?: (theme: string) => boolean;
}

const ThemeSelection = ({ 
  selectedThemes,
  onThemeChange, 
  customThemes,
  onCustomThemeChange,
  disabledThemes
}: ThemeSelectionProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [newCustomTheme, setNewCustomTheme] = useState("");
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: canUseCustomFields } = useQuery({
    queryKey: ['featureCheck', 'custom_fields'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase.functions.invoke("check-feature-usage", {
        body: { 
          feature_key: "custom_fields",
          user_id: user.id 
        }
      });
      
      if (error) {
        logger.error("Error checking custom fields feature:", error);
        return false;
      }
      
      return data?.can_use || false;
    },
    initialData: false
  });

  const handleThemeToggle = (theme: string) => {
    if (selectedThemes.includes(theme)) {
      onThemeChange(selectedThemes.filter((env) => env !== theme));
    } else {
      onThemeChange([...selectedThemes, theme]);
    }
  };

  const handleAddCustomTheme = () => {
    if (!canUseCustomFields) {
      setIsDialogOpen(true);
      return;
    }

    if (newCustomTheme.trim() && !customThemes.includes(newCustomTheme.trim())) {
      onCustomThemeChange([...customThemes, newCustomTheme.trim()]);
      setNewCustomTheme("");
    }
  };

  const handleRemoveCustomTheme = (themeToRemove: string) => {
    onCustomThemeChange(customThemes.filter(theme => theme !== themeToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!canUseCustomFields) {
        setIsDialogOpen(true);
        return;
      }
      handleAddCustomTheme();
    }
  };

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-2 hover:bg-accent"
      >
        <span className="font-semibold text-left flex-1">{t('storySetup.basicInfo.theme')}</span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </Button>

      <div className={cn("space-y-4", !isExpanded && "hidden")}>
        <ScrollingCarousel>
          {THEMES.map((theme) => (
            <TooltipProvider key={theme}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant={selectedThemes.includes(theme) ? "default" : "outline"}
                    onClick={() => handleThemeToggle(theme)}
                    className={cn(
                      "whitespace-nowrap text-sm py-1 px-3 h-8",
                      disabledThemes?.(theme) && "opacity-50 cursor-not-allowed"
                    )}
                    disabled={disabledThemes?.(theme)}
                  >
                    {t(`storySetup.themeOptions.${theme}`)}
                  </Button>
                </TooltipTrigger>
                {disabledThemes?.(theme) && (
                  <TooltipContent>
                    <p>{t('settings.subscription.unlockTheme')}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          ))}
        </ScrollingCarousel>

        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              value={newCustomTheme}
              onChange={(e) => setNewCustomTheme(e.target.value)}
              placeholder={t('storySetup.basicInfo.addCustomTheme')}
              className="flex-1"
              onKeyPress={handleKeyPress}
              disabled={!canUseCustomFields}
              maxLength={500}
            />
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    type="button"
                    onClick={() => {
                      if (!canUseCustomFields) {
                        setIsDialogOpen(true);
                      } else {
                        handleAddCustomTheme();
                      }
                    }}
                    variant="outline" 
                    size="icon" 
                    className={canUseCustomFields ? "" : "opacity-50"}
                  >
                    {canUseCustomFields ? (
                      <Plus className="w-4 h-4" />
                    ) : (
                      <Lock className="w-4 h-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                {!canUseCustomFields && (
                  <TooltipContent>
                    <p>{t('settings.subscription.upgradeForCustomThemes')}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t('settings.subscription.unlockCustomThemes')}</DialogTitle>
                <DialogDescription>
                  {t('settings.subscription.upgradeForCustomThemesDescription')}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-primary" />
                  <span>{t('settings.subscription.addCustomThemesFeature')}</span>
                </div>
              </div>
              <DialogFooter className="sm:justify-start">
                <Button
                  type="button"
                  onClick={() => {
                    setIsDialogOpen(false);
                    navigate("/dashboard/more");
                  }}
                  className="w-full sm:w-auto"
                >
                  {t('settings.subscription.upgradeNow')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {customThemes.length > 0 && (
          <ScrollingCarousel>
            {customThemes.map((theme) => (
              <Button
                key={theme}
                type="button"
                variant="default"
                onClick={(e) => handleRemoveCustomTheme(theme)}
                className="whitespace-nowrap text-sm py-1 px-3 h-8"
              >
                {theme} ×
              </Button>
            ))}
          </ScrollingCarousel>
        )}
      </div>
    </div>
  );
};

export default ThemeSelection;
